<template>
    <CicPopup
        :saveable="canEdit || canEditV2UI || canEditV3UI"
        :title="
            chatAccountDetails.accountId
                ? chatAccountDetails.userInterfaceUrl === null
                    ? $t('Edit API Account')
                    : $t('Edit User Interface Account')
                : $t('New Chat API Account')
        "
        class="cic-popup-custom-height"
        :help-text="$t('DESC_EDIT_CHAT')"
        @blur="$emit('blur')"
        @input="saveChatAccountDetails"
    >
        <ul class="item-form">
            <li class="form-label-first">{{ $t('Account Name') }}</li>
            <li>
                <CicTextbox
                    id="accountName"
                    :enabled="canEdit || canEditV2UI || canEditV3UI"
                    :value="chatAccountDetails.accountName"
                    :class="{ highlight: !validation.accountName }"
                    :maxlength="64"
                    @input="(val) => updateFieldValue('chatAccountDetails', 'accountName', val)"
                />
            </li>
            <template v-if="!chatAccountDetails.accountId && canEditV2UI">
                <li class="form-label">{{ $t('Create V2 User Interface') }}</li>
                <li>
                    <CicCheckbox
                        :value="chatAccountDetails.hasUserInterface"
                        @input="(val) => updateFieldValue('chatAccountDetails', 'hasUserInterface', val)"
                    />
                </li>
            </template>
            <template v-if="!chatAccountDetails.accountId && canEditV3UI">
                <li class="form-label">{{ $t('Create V3 User Interface') }}</li>
                <li>
                    <CicCheckbox
                        :value="chatAccountDetails.hasV3Interface"
                        @input="(val) => updateFieldValue('chatAccountDetails', 'hasV3Interface', val)"
                    />
                </li>
            </template>
            <template v-if="chatParameters != null && chatAccountDetails.hasV3Interface">
                <li class="form-label">{{ $t('Attachments') }}</li>
                <li>
                    <CicCheckbox
                        :value="chatParameters.AllowAttachments"
                        :enabled="canEdit || canEditV2UI || canEditV3UI"
                        @input="(val) => updateFieldValue('chatParameters', 'AllowAttachments', val)"
                    />
                </li>
                <li class="form-label">{{ $t("Show Agent Name") }}</li>
                <li>
                    <CicCheckbox
                        :value="chatParameters.ShowAgentsName"
                        :enabled="canEdit || canEditV2UI || canEditV3UI"
                        @input="(val) => updateFieldValue('chatParameters', 'ShowAgentsName', val)"
                    />
                </li>
                <li class="form-label">{{ $t('Clickable URLs') }}</li>
                <li>
                    <CicCheckbox
                        :value="chatParameters.ClickableUrls"
                        :enabled="canEdit || canEditV2UI || canEditV3UI"
                        @input="(val) => updateFieldValue('chatParameters', 'ClickableUrls', val)"
                    />
                </li>
                <li class="form-label">{{ $t('Email Transcripts') }}</li>
                <li>
                    <CicCheckbox
                        :value="chatParameters.AllowEmailTranscript"
                        :enabled="canEdit || canEditV2UI || canEditV3UI"
                        @input="(val) => updateFieldValue('chatParameters', 'AllowEmailTranscript', val)"
                    />
                </li>
                <template v-if="chatParameters.AllowEmailTranscript">
                    <li class="form-label-first">{{ $t('Transcript Settings') }}</li>
                    <p>{{ $t('Provide the following fields to configure Transcript Settings') }}</p>
                    <li class="form-label">{{ $t('Email Address') }}</li>
                    <li>
                        <CicTextbox
                            id="senderEmailAddress"
                            :enabled="canEdit || canEditV2UI || canEditV3UI"
                            :value="chatParameters.TranscriptSettings.senderEmailAddress"
                            :class="{ highlight: !validation.senderEmailAddress }"
                            :maxlength="128"
                            @input="(val) => updateTranscriptSettings('senderEmailAddress', val)"
                        />
                    </li>
                    <li class="form-label">{{ $t('Subject') }}</li>
                    <li>
                        <CicTextbox
                            id="emailSubject"
                            :enabled="canEdit || canEditV2UI || canEditV3UI"
                            :value="chatParameters.TranscriptSettings.emailSubject"
                            :class="{ highlight: !validation.emailSubject }"
                            :maxlength="256"
                            @input="(val) => updateTranscriptSettings('emailSubject', val)"
                        />
                    </li>
                </template>
            </template>
            <li v-if="surveys.length > 0">
                <CSATSurveyOptions
                    :chat-account-details="chatAccountDetails"
                    :surveys="surveys"
                    :can-edit="canEdit"
                    @change="updateFieldValue"
                />
            </li>
            <template v-if="chatAccountDetails.accountId > 0 && chatAccountDetails.userInterfaceUrl === null">
                <li class="form-label">{{ $t('Webhook URL') }}</li>
                <li>
                    <CicTextbox
                        id="accountName"
                        :enabled="canEdit"
                        :value="chatAccountDetails.webhookUrl"
                        :class="{ highlight: !validation.webhookUrl }"
                        :maxlength="128"
                        @input="(val) => updateFieldValue('chatAccountDetails', 'webhookUrl', val)"
                    />
                </li>
                <h3>{{ $t('Webhook Subscriptions') }}</h3>
                <li class="form-label">{{ $t('Message Events') }}</li>
                <li>
                    <CicCheckbox
                        :value="chatAccountDetails.subscribedEvents.includes('Message')"
                        :enabled="canEdit"
                        @input="() => toggleSubscribedEvent('Message')"
                    />
                </li>
                <li class="form-label">{{ $t('Typing Events') }}</li>
                <li>
                    <CicCheckbox
                        :value="chatAccountDetails.subscribedEvents.includes('Typing')"
                        :enabled="canEdit"
                        @input="() => toggleSubscribedEvent('Typing')"
                    />
                </li>
                <li class="form-label">{{ $t('Interaction Status Events') }}</li>
                <li>
                    <CicCheckbox
                        :value="chatAccountDetails.subscribedEvents.includes('InteractionStatus')"
                        :enabled="canEdit"
                        @input="() => toggleSubscribedEvent('InteractionStatus')"
                    />
                </li>
            </template>
        </ul>
    </CicPopup>
</template>

<script>
    import CicPopup from '@/components/CicPopup.vue';
    import CicTextbox from '@/components/inputs/CicTextbox';
    import CicCheckbox from '@/components/inputs/CicCheckbox';
    import { showValidationMessage } from '@/utils/Notification';
    import CSATSurveyOptions from '@/feature/csatSurveyOptions/CSATSurveyOptions';

    export default {
        name: 'EditChatAPIAccount',
        components: {
            CSATSurveyOptions,
            CicCheckbox,
            CicPopup,
            CicTextbox,
        },
        props: {
            chatAccountDetails: {
                type: Object,
                required: true,
            },
            chatParameters: {
                type: [Object, null],
                required: true,
            },
            surveys: {
                type: Array,
                required: true,
            },
        },
        emits: ['blur', 'change', 'input'],
        data() {
            return {
                validation: {
                    valueUpdated: false,
                    accountName: true,
                    webhookUrl: true,
                    allowAttachments: true,
                    showAgentsName: true,
                    clickableUrls: true,
                    allowEmailTranscript: true,
                    transcriptSettings: true,
                    senderEmailAddress: true,
                    emailSubject: true,
                },
            };
        },
        computed: {
            canEdit() {
                return this.$store.hasRight('EDIT_ACCESS_TYPE_26');
            },
            canEditV2UI() {
                return this.$store.hasRight('EDIT_ACCESS_TYPE_26_UI');
            },
            canEditV3UI() {
                return this.$store.hasRight('EDIT_ACCESS_TYPE_26_V3UI');
            },
        },
        methods: {
            updateTranscriptSettings(setting, value) {
                this.validation.valueUpdated = true; // record that a value has been changed.

                let tmp = { ...this.chatParameters.TranscriptSettings };
                tmp[setting] = value;

                this.$emit('change', 'chatParameters', 'TranscriptSettings', tmp);
            },
            updateFieldValue(object, field, val) {
                this.validation.valueUpdated = true; // record that a value has been changed.

                if (field === 'hasUserInterface' && val) {
                    this.$emit('change', object, 'hasUserInterface', true);
                    this.$emit('change', object, 'hasV3Interface', false);
                } else if (field === 'hasV3Interface' && val) {
                    this.$emit('change', object, 'hasUserInterface', false);
                    this.$emit('change', object, 'hasV3Interface', true);
                } else {
                    this.$emit('change', object, field, val);
                }
            },
            toggleSubscribedEvent(event) {
                this.validation.valueUpdated = true; // record that a value has been changed.

                let subscribedEvents = JSON.parse(JSON.stringify(this.chatAccountDetails.subscribedEvents));
                if (subscribedEvents.includes(event)) {
                    subscribedEvents = subscribedEvents.filter((e) => e !== event);
                } else {
                    subscribedEvents.push(event);
                }
                this.$emit('change', 'chatAccountDetails', 'subscribedEvents', subscribedEvents);
            },
            isValidEmail(email) {
                const emailPattern = /^[\w-.]+@([\w-]+.)+[\w-]{2,4}$/;
                return emailPattern.test(email);
            },
            saveChatAccountDetails() {
                this.validation.accountName = true;
                this.validation.webhookUrl = true;
                this.validation.allowAttachments = true;
                this.validation.showAgentsName = true;
                this.validation.clickableUrls = true;
                this.validation.allowEmailTranscript = true;
                this.validation.transcriptSettings = true;
                this.validation.senderEmailAddress = true;
                this.validation.emailSubject = true;

                if (this.chatAccountDetails.accountName.trim() === '') {
                    this.validation.accountName = false;
                    showValidationMessage('Account Name is a required field');
                    return;
                }

                const invalidCharsRegex = /[/"'\-.#&?|[\](){}]/;
                if (invalidCharsRegex.test(this.chatAccountDetails.accountName.trim())) {
                    this.validation.accountName = false;
                    showValidationMessage(
                        'Please ensure that Account Name does not contain the characters: / \\ " \' - . # & ? | [ ] ( ) { }',
                    );
                    return;
                }

                if (this.chatAccountDetails.accountName.length > 64) {
                    this.validation.accountName = false;
                    showValidationMessage('Please specify an Account Name that is at most 64 characters');
                    return;
                }

                if (
                    this.chatAccountDetails.subscribedEvents.length > 0 &&
                    (!this.chatAccountDetails.webhookUrl || this.chatAccountDetails.webhookUrl.trim() === '')
                ) {
                    this.validation.webhookUrl = false;
                    showValidationMessage('Webhook URL must be set when subscribing to events');
                    return;
                }

                if (this.chatAccountDetails.webhookUrl && this.chatAccountDetails.webhookUrl.trim() !== '') {
                    const urlRegex = /^(http|https):\/\/[^ "]+$/;
                    if (!urlRegex.test(this.chatAccountDetails.webhookUrl.trim())) {
                        this.validation.webhookUrl = false;
                        showValidationMessage('Webhook URL is not a valid URL');
                        return;
                    }
                }

                if (this.chatParameters && this.chatParameters.AllowEmailTranscript) {
                    // Validate transcript settings if necessary
                    const settings = this.chatParameters.TranscriptSettings;
                    if (!settings.senderEmailAddress || settings.senderEmailAddress.trim() === '') {
                        this.validation.senderEmailAddress = false;
                        showValidationMessage('Email Address is a required field');
                        return;
                    }
                    if (!this.isValidEmail(settings.senderEmailAddress)) {
                        this.validation.senderEmailAddress = false;
                        showValidationMessage('Invalid Email Address');
                        return;
                    }
                    if (!settings.emailSubject || settings.emailSubject.trim() === '') {
                        this.validation.emailSubject = false;
                        showValidationMessage('Subject is a required field');
                        return;
                    }
                }

                if (!this.validation.valueUpdated) {
                    showValidationMessage('No changes to save');
                    return;
                }

                this.$emit('input');
                this.validation.valueUpdated = false;
            },
        },
    };
</script>
